import { useCallback, useEffect, useRef, useState } from 'react';

const HOLD_MS = 420;

/**
 * Press-and-hold. The handoff screens are the integrity mechanism of one-phone
 * play, and a tap is one fumble away from burning the round — so the default
 * gesture stays a 420ms hold (settled at v1 Gate A).
 *
 * Three things the v1 implementation got wrong, all fixed here:
 *
 * 1. `click` commits immediately. VoiceOver's double-tap dispatches an
 *    activation event, not a pointerdown/hold pair, and v1's
 *    `preventDefault()` on pointerdown suppressed the compatibility click —
 *    so a VoiceOver, Switch Control or TalkBack user could not advance the
 *    game at all. An AT activation is already deliberate; 420ms of held
 *    contact on top of it protects nothing.
 * 2. Two aborted holds reveal a plain tap-to-continue button. Tremor,
 *    spasticity and cold hands all produce short holds, and v1's only
 *    feedback for a 380ms attempt was nothing happening.
 * 3. Under `prefers-reduced-motion` the fill steps 33/66/100 instead of
 *    jumping straight to full. Reduced motion should remove decoration, not
 *    state information — this is a progress meter, not an animation.
 */
export function HoldButton({
  label,
  onComplete,
  describedBy,
}: {
  label: string;
  onComplete: () => void;
  describedBy?: string;
}) {
  const [arming, setArming] = useState(false);
  const [aborts, setAborts] = useState(0);
  const [step, setStep] = useState(0);
  const timer = useRef<number | null>(null);
  const stepTimer = useRef<number | null>(null);
  const armedAt = useRef(0);
  const done = useRef(false);
  /** Set by a real pointer press, so the click it generates is not a shortcut. */
  const fromPointer = useRef(false);

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const clearTimers = useCallback(() => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    if (stepTimer.current !== null) window.clearInterval(stepTimer.current);
    timer.current = null;
    stepTimer.current = null;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const commit = useCallback(() => {
    if (done.current) return;
    done.current = true;
    fromPointer.current = false;
    clearTimers();
    setArming(false);
    onComplete();
  }, [clearTimers, onComplete]);

  const begin = useCallback(
    (ev: React.PointerEvent<HTMLButtonElement>) => {
      ev.preventDefault();
      if (done.current || arming) return;
      fromPointer.current = true;
      armedAt.current = Date.now();
      setArming(true);
      setStep(0);
      if (reduced) {
        let s = 0;
        stepTimer.current = window.setInterval(() => {
          s += 1;
          setStep(s);
          if (s >= 3) clearTimers();
        }, HOLD_MS / 3);
      }
      timer.current = window.setTimeout(commit, HOLD_MS);
    },
    [arming, clearTimers, commit, reduced],
  );

  const cancel = useCallback(() => {
    if (done.current || !arming) return;
    clearTimers();
    setArming(false);
    setStep(0);
    if (Date.now() - armedAt.current < HOLD_MS) setAborts((n) => n + 1);
  }, [arming, clearTimers]);

  /**
   * The click path exists ONLY for assistive technology. `preventDefault()` on
   * pointerdown does not suppress the compatibility click, so wiring `commit`
   * straight to `onClick` would let any quick tap skip the 420ms hold — which
   * is the integrity mechanism of one-phone play, not decoration.
   *
   * An AT activation (VoiceOver double-tap, Switch Control, a synthetic click)
   * arrives with `detail === 0` and no preceding pointer press. A finger tap
   * arrives with `detail === 1` after a pointerdown we have already seen.
   */
  const onClick = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      if (ev.detail === 0 && !fromPointer.current) commit();
      fromPointer.current = false;
    },
    [commit],
  );

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLButtonElement>) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        commit();
      }
    },
    [commit],
  );

  // No inline style attributes anywhere in this app: the production CSP has no
  // `style-src-attr 'unsafe-inline'`, and a style attribute would be dropped.
  return (
    <>
      <button
        type="button"
        className={`hold${arming ? ' arming' : ''}`}
        aria-describedby={describedBy}
        onPointerDown={begin}
        onPointerUp={cancel}
        onPointerLeave={cancel}
        onPointerCancel={cancel}
        onKeyDown={onKeyDown}
        onClick={onClick}
      >
        <span className="hold-fill" data-step={reduced ? step : undefined} />
        <span>{label}</span>
      </button>
      {aborts >= 2 && (
        <button type="button" className="btn ghost hold-escape" onClick={() => commit()}>
          Hold not working? Tap here
        </button>
      )}
    </>
  );
}
